import { createContext, useContext, useEffect, useMemo } from 'react';
import { BrandConfig, loadBrandConfig } from '../config/brand.config';

const BrandContext = createContext<BrandConfig | null>(null);

function applyBrandCssVars(brand: BrandConfig): void {
  const root = document.documentElement;
  const s = brand.primaryScale;

  root.style.setProperty('--brand-primary-50', s[50]);
  root.style.setProperty('--brand-primary-100', s[100]);
  root.style.setProperty('--brand-primary-200', s[200]);
  root.style.setProperty('--brand-primary-300', s[300]);
  root.style.setProperty('--brand-primary-400', s[400]);
  root.style.setProperty('--brand-primary-500', s[500]);
  root.style.setProperty('--brand-primary-600', s[600]);
  root.style.setProperty('--brand-primary-700', s[700]);
  root.style.setProperty('--brand-primary-800', s[800]);
  root.style.setProperty('--brand-primary-900', s[900]);
  root.style.setProperty('--brand-primary-950', s[950]);
  root.style.setProperty('--brand-accent', brand.accentColor);
  root.style.setProperty('--brand-surface-bg', brand.surfaceBase);
  root.style.setProperty('--brand-shadow-tint', brand.shadowTint);
}

function updateFavicon(faviconUrl: string): void {
  let link = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (link) {
    link.href = faviconUrl;
  } else {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/svg+xml';
    link.href = faviconUrl;
    document.head.appendChild(link);
  }
}

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const brand = useMemo(() => loadBrandConfig(), []);

  useEffect(() => {
    applyBrandCssVars(brand);
    document.title = brand.browserTitle;
    updateFavicon(brand.faviconUrl);
    const themeMeta = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
    if (themeMeta) themeMeta.content = brand.primaryColor;
  }, [brand]);

  return (
    <BrandContext.Provider value={brand}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand(): BrandConfig {
  const ctx = useContext(BrandContext);
  if (!ctx) throw new Error('useBrand must be used within BrandProvider');
  return ctx;
}
